/**
 * Privacy Policy Page
 * 
 * GDPR-compliant privacy policy for the Pleasance platform.
 */

import React from 'react';

function PrivacyPage() {
    return (
        <div className="min-h-screen bg-charcoal px-4 py-12">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <h1 className="text-4xl font-serif text-wine-400 mb-2">Privacy Policy</h1>
                <p className="text-ivory-500 text-sm mb-8">Last updated: January 18, 2026</p>

                <div className="prose prose-invert prose-wine max-w-none space-y-8 text-ivory-300">

                    {/* Introduction */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">Introduction</h2>
                        <p>
                            Pleasance ("we", "us", or "our") respects your privacy and is committed to protecting your personal data.
                            This privacy policy explains how we handle any information when you use our Platform.
                        </p>
                    </section>

                    {/* Section 1 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">1. Information We Collect</h2>
                        <p>
                            We practice <strong>data minimization</strong>. We do not require account registration to browse our content.
                        </p>
                        <h3 className="text-lg text-ivory-200 mt-4 mb-2">Automatically Collected:</h3>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>Age verification confirmation (stored locally in your browser)</li>
                            <li>Anonymous usage analytics (page views, not personal data)</li>
                            <li>Technical information (browser type, device type)</li>
                        </ul>
                        <h3 className="text-lg text-ivory-200 mt-4 mb-2">What We Do NOT Collect:</h3>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>Your real name or identity</li>
                            <li>Email addresses (unless you contact us)</li>
                            <li>Browsing history or specific page views tied to identifiable information</li>
                            <li>Sexual preferences or kink interests</li>
                        </ul>
                    </section>

                    {/* Section 2 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">2. How We Use Information</h2>
                        <p>Any information we collect is used solely to:</p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Ensure you have verified your age</li>
                            <li>Improve the Platform's functionality and content</li>
                            <li>Respond to your inquiries if you contact us</li>
                        </ul>
                    </section>

                    {/* Section 3 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">3. Cookies</h2>
                        <p>
                            We use minimal, essential cookies:
                        </p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li><strong>Age verification</strong>: Remembers your 18+ confirmation</li>
                            <li><strong>Analytics</strong>: Privacy-respecting analytics (no personal tracking)</li>
                        </ul>
                        <p className="mt-4">
                            You can disable cookies in your browser settings, but this may affect Platform functionality.
                        </p>
                    </section>

                    {/* Section 4 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">4. Third-Party Services</h2>
                        <p>
                            We may use third-party services for analytics (e.g., Plausible Analytics) that are
                            designed to be privacy-respecting and GDPR-compliant.
                        </p>
                    </section>

                    {/* Section 5 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">5. Your Rights (GDPR)</h2>
                        <p>If you are in the European Union, you have the right to:</p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Access any personal data we hold about you</li>
                            <li>Request correction of inaccurate data</li>
                            <li>Request deletion of your data</li>
                            <li>Object to processing of your data</li>
                            <li>Data portability</li>
                        </ul>
                        <p className="mt-4">
                            To exercise these rights, contact us at <span className="text-wine-400">privacy@pleasance.app</span>.
                        </p>
                    </section>

                    {/* Section 6 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">6. Data Security</h2>
                        <p>
                            We implement appropriate technical and organizational measures to protect any data we process.
                            However, no electronic transmission or storage is 100% secure.
                        </p>
                    </section>

                    {/* Section 7 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">7. Changes to This Policy</h2>
                        <p>
                            We may update this privacy policy from time to time. Changes will be posted on this page
                            with an updated "Last updated" date.
                        </p>
                    </section>

                    {/* Contact */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">8. Contact Us</h2>
                        <p>
                            For privacy-related inquiries: <span className="text-wine-400">privacy@pleasance.app</span>
                        </p>
                    </section>

                </div>

                {/* Footer */}
                <div className="mt-12 pt-8 border-t border-charcoal-700">
                    <p className="text-ivory-600 text-sm text-center">
                        © 2026 Pleasance. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default PrivacyPage;
